import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

// Reusable scratch quaternions used by syncMeshFromBody to avoid per-frame
// allocations. The frame loop calls syncMeshFromBody for every card.
const _bodyQuat = new THREE.Quaternion();
const _intentQuat = new THREE.Quaternion();

// Match the visible table mesh in tableScene.js.
const TABLE_Y = 0.05;

// Rapier must be initialized asynchronously (WASM load). This promise
// resolves once the WASM binary is ready. createPhysicsWorld awaits it.
let rapierReady = false;
const initPromise = RAPIER.init().then(() => { rapierReady = true; });

// Body type constants. GameBoard references these via CANNON.Body.*,
// so we export equivalents to keep the API identical.
export const BodyType = {
  DYNAMIC: 0,
  STATIC: 1,
  KINEMATIC: 2,
};
export const SleepState = {
  AWAKE: 0,
  SLEEPING: 1,
};

/**
 * Wrapper around a Rapier RigidBody that exposes the same property
 * interface GameBoard expects (position, quaternion, velocity,
 * angularVelocity, sleepState, type, collisionResponse, etc.).
 */
class BodyProxy {
  constructor(rigidBody, colliderHandle, world) {
    this._rb = rigidBody;
    this._colliderHandle = colliderHandle;
    this._world = world;
    this._collisionResponse = true;
    // Last pose both sides are known to agree on (applied from a remote
    // release or broadcast via our own release). Used by tickPhysicsSync
    // to distinguish "body woke but didn't move" (skip broadcast) from
    // "body drifted after apply" (needs correction re-broadcast).
    this._lastSyncedPose = null;
    this._dropTime = 0;
    this.position = { x: 0, y: 0, z: 0, set(x, y, z) { this.x = x; this.y = y; this.z = z; } };
    this.quaternion = { x: 0, y: 0, z: 0, w: 1, set(x, y, z, w) { this.x = x; this.y = y; this.z = z; this.w = w; } };
    this.velocity = { x: 0, y: 0, z: 0, set(x, y, z) { this.x = x; this.y = y; this.z = z; } };
    this.angularVelocity = { x: 0, y: 0, z: 0, set(x, y, z) { this.x = x; this.y = y; this.z = z; } };
    this._halfExtentsY = 0.16;
    this._syncFromRapier();
  }

  get type() {
    if (this._rb.isFixed()) return BodyType.STATIC;
    if (this._rb.isKinematic()) return BodyType.KINEMATIC;
    return BodyType.DYNAMIC;
  }
  set type(t) {
    if (t === BodyType.STATIC) {
      this._rb.setBodyType(RAPIER.RigidBodyType.Fixed, true);
    } else if (t === BodyType.KINEMATIC) {
      this._rb.setBodyType(RAPIER.RigidBodyType.KinematicPositionBased, true);
    } else {
      this._rb.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
    }
  }

  get sleepState() {
    return this._rb.isSleeping() ? SleepState.SLEEPING : SleepState.AWAKE;
  }

  get collisionResponse() { return this._collisionResponse; }
  set collisionResponse(v) {
    this._collisionResponse = v;
    // In Rapier, disabling collision response is done via collision groups.
    // Membership=0 means the collider doesn't interact with anything.
    const collider = this._world.getCollider(this._colliderHandle);
    if (collider) {
      if (v) {
        // Group 1 membership, interacts with group 1
        collider.setCollisionGroups(0x00010001);
      } else {
        // Group 0 membership, interacts with nothing
        collider.setCollisionGroups(0x00000000);
      }
    }
  }

  sleep() { this._rb.sleep(); }
  wakeUp() { this._rb.wakeUp(); }
  setGravityScale(scale, wakeUp = false) { this._rb.setGravityScale(scale, wakeUp); }

  // Pull Rapier state into JS-side fields (called after step)
  _syncFromRapier() {
    const t = this._rb.translation();
    this.position.x = t.x; this.position.y = t.y; this.position.z = t.z;
    const r = this._rb.rotation();
    this.quaternion.x = r.x; this.quaternion.y = r.y;
    this.quaternion.z = r.z; this.quaternion.w = r.w;
    const v = this._rb.linvel();
    this.velocity.x = v.x; this.velocity.y = v.y; this.velocity.z = v.z;
    const w = this._rb.angvel();
    this.angularVelocity.x = w.x; this.angularVelocity.y = w.y; this.angularVelocity.z = w.z;
  }

  // Push JS-side fields into Rapier. Called once per frame from
  // stepPhysics + at every body-type transition. The path differs by
  // mode:
  //
  //   * Kinematic + collisionResponse=true (a local drag): use
  //     setNextKinematicTranslation / setNextKinematicRotation so the
  //     next world.step() *sweeps* the body from its current pose to
  //     the target pose, running CCD + contact resolution along the
  //     way. setTranslation here would teleport and skip the sweep
  //     entirely. The bug that let dragged cards pass straight
  //     through resting cards and end up interpenetrating them on
  //     release.
  //   * Kinematic + collisionResponse=false (remote peer's card under
  //     our interpolation): teleport is correct. Remote pose is
  //     already smoothed on the JS side and the body ignores contacts
  //     anyway.
  //   * Dynamic: explicit pose set, teleport with wake, overwrite
  //     velocities.
  _pushToRapier() {
    const pos = { x: this.position.x, y: this.position.y, z: this.position.z };
    const rot = { x: this.quaternion.x, y: this.quaternion.y, z: this.quaternion.z, w: this.quaternion.w };

    if (this._rb.isKinematic() && this._collisionResponse) {
      this._rb.setNextKinematicTranslation(pos);
      this._rb.setNextKinematicRotation(rot);
      return;
    }

    this._rb.setTranslation(pos, true);
    this._rb.setRotation(rot, true);
    this._rb.setLinvel({ x: this.velocity.x, y: this.velocity.y, z: this.velocity.z }, true);
    this._rb.setAngvel({ x: this.angularVelocity.x, y: this.angularVelocity.y, z: this.angularVelocity.z }, true);
  }
}

// Track all proxies so stepPhysics can sync them.
let _allProxies = [];

export async function createPhysicsWorld() {
  if (!rapierReady) await initPromise;

  // Earth gravity. Matches realms.cards (a sibling Sorcery TCG client)
  // and is gentler than our previous -80 (~8× Earth). Lower velocities
  // mean less tunneling pressure, smaller contact-skin overshoot in
  // resting stacks, and easier sleep convergence. Every drift / clip
  // class we've fought scaled with gravity.
  const gravity = { x: 0, y: -9.81, z: 0 };
  const world = new RAPIER.World(gravity);

  // Tune the solver for thin-plate stacks. The defaults (4 PGS iterations,
  // 1 substep, 1 CCD substep) aren't enough to resolve a card landing at
  // a narrow overlap against another card before the frame ends. The
  // solver bails mid-penetration and two cards end up visibly clipped.
  // Doubling iterations + enabling extra CCD substeps removes the
  // "cards fall into each other" bug without measurable perf cost.
  world.integrationParameters.numSolverIterations = 8;
  world.integrationParameters.numInternalPgsIterations = 2;
  world.integrationParameters.maxCcdSubsteps = 2;

  // Ground plane. A very thin, very wide static cuboid at TABLE_Y.
  const groundDesc = RAPIER.RigidBodyDesc.fixed()
    .setTranslation(0, TABLE_Y - 0.25, 0);
  const groundBody = world.createRigidBody(groundDesc);
  const groundCollider = RAPIER.ColliderDesc.cuboid(200, 0.25, 200)
    .setFriction(0.7)
    .setRestitution(0.02);
  world.createCollider(groundCollider, groundBody);

  // Reset proxy list
  _allProxies = [];

  // Attach a reference so callers can access the raw Rapier world
  world._rapier = RAPIER;
  world._proxies = _allProxies;

  return world;
}

// Hard cap on the dt we feed into the world, regardless of what the
// frame loop reports. A 2-second GC pause or a tab-return from
// background would otherwise advance physics by 2s in one go and
// teleport cards (or worse, skip every collision on the way). 1/30 is
// the slowest rate we're willing to simulate in a single step. If a
// real frame took longer, we just drop the extra time on the floor.
const MAX_STEP_DT = 1 / 30;

export function stepPhysics(world, dt = 1 / 60) {
  // Rapier reads dt from integrationParameters, not world.step(...)
  // arguments. Syncing the measured frame dt here lets a 144Hz monitor
  // get true 144Hz physics. And a dropped frame get a single bigger
  // step instead of silently drifting behind wall-clock time.
  const stepDt = Number.isFinite(dt) && dt > 0 ? Math.min(dt, MAX_STEP_DT) : 1 / 60;
  world.integrationParameters.dt = stepDt;

  // Push any JS-side mutations (from moveKinematicBody, etc.) to Rapier.
  // Restricted to kinematic bodies because their next pose is driven
  // from JS. Drag targets via setNextKinematicTranslation, and remote-
  // controlled cards via teleport. Dynamic bodies are owned by Rapier:
  // pushing back per-frame called setTranslation(pos, wakeUp=true) on
  // every resting card, force-waking it before each step. Sleep never
  // stuck, and accumulated micro-contact resolution caused stacks to
  // slowly drift and rotate ("crooked cards" bug). Explicit dynamic
  // transitions (setBodyDynamic, applyRemotePoseAndRest, freeze/unfreeze)
  // call _pushToRapier directly at the moment the JS state actually
  // changes, so they remain unaffected.
  for (const proxy of _allProxies) {
    if (proxy._rb.isKinematic()) proxy._pushToRapier();
  }

  world.step();

  // Pull Rapier results back to JS-side proxies
  for (const proxy of _allProxies) {
    proxy._syncFromRapier();
  }
}

// -90° rotation around body-local Y, pre-computed so we don't rebuild
// the quaternion on every tap. Matches the mesh intentEuler's tap
// convention (computeTargetZ subtracts PI/2 from intent.z, which after
// the lay-flat X rotation becomes -PI/2 around world Y).
const LANDSCAPE_COLLIDER_QUAT = Object.freeze({
  x: 0,
  y: -0.7071067811865475,
  z: 0,
  w: 0.7071067811865476,
});
const PORTRAIT_COLLIDER_QUAT = Object.freeze({ x: 0, y: 0, z: 0, w: 1 });

export function addCardBody(world, mesh, { width, height, thickness, landscape = false }) {
  // Cards are inert: kinematic body type (never simulated by the
  // physics integrator) plus a sensor collider (no contact response).
  // The body exists only as a position/rotation container that drag
  // handlers can write to and tickPhysicsSync's broadcast loop can
  // read from. Stacking, drift, sleep, and collision response are
  // dead concerns. Y is computed from cardInstance.interactionOrder
  // by GameBoard's _applyComputedY, so physics has nothing to do.
  const bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased()
    .setTranslation(mesh.position.x, mesh.position.y, mesh.position.z)
    .setRotation({ x: 0, y: 0, z: 0, w: 1 })
    .setCanSleep(false);

  const rigidBody = world.createRigidBody(bodyDesc);

  const colliderDesc = RAPIER.ColliderDesc.cuboid(width / 2, thickness / 2, height / 2)
    .setSensor(true)
    .setCollisionGroups(0x00000000);

  // Sites and tapped cards are visually rotated 90° around the world-Y
  // axis via mesh.userData.intentEuler. The intent is a pure display
  // transform. Physics doesn't see it. Rotate the collider WrtParent
  // so the physics footprint matches the visual: W maps to world Z and
  // H to world X. Without this, a landscape card has a portrait collider
  // and dropped cards can settle inside the visual-but-physics-empty
  // strip on the long edges.
  if (landscape) {
    colliderDesc.setRotation(LANDSCAPE_COLLIDER_QUAT);
  }

  const collider = world.createCollider(colliderDesc, rigidBody);

  const proxy = new BodyProxy(rigidBody, collider.handle, world);
  proxy._halfExtentsY = thickness / 2;
  proxy._landscape = landscape;
  _allProxies.push(proxy);
  mesh.userData.body = proxy;
  return proxy;
}

// Rotate the collider's local frame to match a visual tap/untap. Called
// from game-logic when the player taps a card or a site is created.
// Idempotent. Skips the Rapier call when the orientation already
// matches. So it's cheap to call redundantly after drags or sync.
export function setCardBodyLandscape(body, landscape) {
  if (!body) return;
  const next = Boolean(landscape);
  if (body._landscape === next) return;
  body._landscape = next;
  const collider = body._world.getCollider(body._colliderHandle);
  if (!collider) return;
  collider.setRotationWrtParent(next ? LANDSCAPE_COLLIDER_QUAT : PORTRAIT_COLLIDER_QUAT);
  body._rb.wakeUp();
}

export function removeCardBody(world, mesh) {
  const proxy = mesh.userData?.body;
  if (!proxy) return;
  world.removeRigidBody(proxy._rb);
  const idx = _allProxies.indexOf(proxy);
  if (idx >= 0) _allProxies.splice(idx, 1);
  mesh.userData.body = null;
}

export function syncMeshFromBody(mesh) {
  const proxy = mesh.userData?.body;
  if (!proxy) return;

  mesh.position.x = proxy.position.x;
  mesh.position.y = proxy.position.y;
  mesh.position.z = proxy.position.z;

  const intent = mesh.userData.intentEuler;
  if (!intent) return;
  _bodyQuat.set(proxy.quaternion.x, proxy.quaternion.y, proxy.quaternion.z, proxy.quaternion.w);
  _intentQuat.setFromEuler(intent);
  mesh.quaternion.multiplyQuaternions(_bodyQuat, _intentQuat);
}

// Update the body's JS-side position cache. The per-frame _pushToRapier
// teleports the Rapier kinematic body to match. Used by drag handlers
// to move a card with the cursor.
export function moveKinematicBody(body, x, y, z) {
  if (!body) return;
  body.position.set(x, y, z);
}

export function applyRemotePoseAndRest(body, pos, quat) {
  // Just teleport. No physics transition. The body is kinematic and
  // the visible Y will be overridden by GameBoard._applyComputedY
  // anyway. We still write pos/quat into the JS proxy so the broadcast
  // loop has the latest pose to read.
  if (!body) return;
  body.position.set(pos[0], pos[1], pos[2]);
  body.quaternion.set(quat[0], quat[1], quat[2], quat[3]);
  body.velocity.set(0, 0, 0);
  body.angularVelocity.set(0, 0, 0);
}

export function addLockConstraint(world, bodyA, bodyB) {
  if (!world || !bodyA || !bodyB) return null;
  // Rapier uses joints. A fixed joint locks two bodies at their current
  // relative pose. Equivalent to cannon-es LockConstraint.
  const anchorA = { x: 0, y: 0, z: 0 };
  const anchorB = {
    x: bodyB.position.x - bodyA.position.x,
    y: bodyB.position.y - bodyA.position.y,
    z: bodyB.position.z - bodyA.position.z,
  };
  const params = RAPIER.JointData.fixed(
    anchorA, { x: 0, y: 0, z: 0, w: 1 },
    anchorB, { x: 0, y: 0, z: 0, w: 1 },
  );
  const joint = world.createImpulseJoint(params, bodyA._rb, bodyB._rb, true);
  return joint;
}

export function removeConstraint(world, constraint) {
  if (!world || !constraint) return;
  world.removeImpulseJoint(constraint, true);
}
